import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { StationV2 } from "@orvis/core";
import { trackEvent } from "../analytics.js";

/**
 * Track what the app is currently playing. One TV station and one radio
 * station can be active at once (opening a TV channel doesn't stop
 * background radio — matches the "sticky now-playing bar" the plan
 * calls out for Phase 5 and works well as a starting point today).
 *
 * `tvPaused` / `radioPaused` track a *real* pause (station stays selected,
 * player element just pauses) as opposed to `playTv(undefined)` /
 * `playRadio(undefined)`, which fully stops and clears the station. The
 * distinction matters for the MediaSession "Now Playing" notification —
 * its pause/play buttons need something to resume into.
 */

interface NowPlayingCtx {
  tv: StationV2 | undefined;
  radio: StationV2 | undefined;
  tvPaused: boolean;
  radioPaused: boolean;
  playTv: (s: StationV2 | undefined) => void;
  playRadio: (s: StationV2 | undefined) => void;
  pauseTv: () => void;
  resumeTv: () => void;
  pauseRadio: () => void;
  resumeRadio: () => void;
}

const Ctx = createContext<NowPlayingCtx | undefined>(undefined);

export function NowPlayingProvider({ children }: { children: ReactNode }) {
  const [tv, setTvState] = useState<StationV2 | undefined>();
  const [tvPaused, setTvPaused] = useState(false);
  const [radio, setRadioState] = useState<StationV2 | undefined>();
  const [radioPaused, setRadioPaused] = useState(false);

  const playTv = useCallback((s: StationV2 | undefined) => {
    setTvState(s);
    setTvPaused(false);
    if (s) trackEvent("select_content", { content_type: "tv", item_id: s.id ?? s.name, item_name: s.name });
  }, []);
  const playRadio = useCallback((s: StationV2 | undefined) => {
    setRadioState(s);
    setRadioPaused(false);
    if (s) trackEvent("select_content", { content_type: "radio", item_id: s.id ?? s.name, item_name: s.name });
  }, []);
  const pauseTv = useCallback(() => setTvPaused(true), []);
  const resumeTv = useCallback(() => setTvPaused(false), []);
  const pauseRadio = useCallback(() => setRadioPaused(true), []);
  const resumeRadio = useCallback(() => setRadioPaused(false), []);

  const value = useMemo<NowPlayingCtx>(
    () => ({
      tv,
      radio,
      tvPaused,
      radioPaused,
      playTv,
      playRadio,
      pauseTv,
      resumeTv,
      pauseRadio,
      resumeRadio,
    }),
    [tv, radio, tvPaused, radioPaused, playTv, playRadio, pauseTv, resumeTv, pauseRadio, resumeRadio]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useNowPlaying(): NowPlayingCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useNowPlaying must be used inside <NowPlayingProvider>");
  return v;
}
